/**
 * Coverage drawer (4.1): opens beside the command center when a clinician calls
 * out. The visits that need cover are the same worklist rows the Compliance tab
 * shows, ordered with the ones that need a competency first; choosing one narrows
 * the panel to that visit and turns its competency filter on, which is how wound
 * VAC shrinks the pool to two people without anybody typing a filter.
 *
 * Ranked candidates in two pools — working today (route slack, drive delta,
 * competency and continuity, ceiling impact in points) and not working, ranked by
 * the cost of the ask in the branch's configured order. No score is shown: the
 * reasons and the pool order are the answer. Reassignment itself stays the product's: drag-and-drop or the optimizer.
 */
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Chip, Collapse, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { POOL_LABELS, selectCalloutCandidates, type Ranked } from "../engine/calloutRanking";
import { useBranchClock } from "./branchClock";
import { selectWorklistRows } from "../engine/worklist";
import { useDemoState } from "../store/useDemoStore";
import { WorklistRows } from "../views/worklist/WorklistRows";
import { useNow } from "./useNow";

export function CoverageDrawer() {
  const { pathname } = useLocation();
  const callOut = useDemoState().presenter.callOut;
  if (!callOut || !pathname.startsWith("/command")) return null;
  return <CoveragePanel providerHandle={callOut.providerHandle} date={callOut.date} startedAtIso={callOut.startedAtIso} />;
}

function CoveragePanel({ providerHandle, date, startedAtIso }: { providerHandle: string; date: string; startedAtIso: string }) {
  const state = useDemoState();
  const now = useNow();
  const tz = state.config.branch.timeZone;
  const [open, setOpen] = useState(true);
  const [requiredTraits, setRequiredTraits] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(undefined);
  // The visit that needs a competency comes first: Ferreira before the rest.
  const unfilled = useMemo(
    () =>
      selectWorklistRows(state, now, { kinds: ["unfilled"] }).sort(
        (a, b) => Number(!!b.requiredTraits?.length) - Number(!!a.requiredTraits?.length) || (a.dueAtIso ?? "").localeCompare(b.dueAtIso ?? ""),
      ),
    [state, now],
  );
  const selectedVisitId = unfilled.find((r) => r.id === selectedRowId)?.realVisitId;
  const ranking = useMemo(
    () => selectCalloutCandidates(state, { providerHandle, date, fromIso: startedAtIso, requiredTraits, visitIds: selectedVisitId === undefined ? undefined : [selectedVisitId] }),
    [state, providerHandle, date, startedAtIso, requiredTraits, selectedVisitId],
  );
  const selectRow = (row: { id: string; requiredTraits?: string[] }) => {
    if (row.id === selectedRowId) {
      setSelectedRowId(undefined);
      setRequiredTraits([]);
      return;
    }
    setSelectedRowId(row.id);
    setRequiredTraits(row.requiredTraits ?? []);
  };
  const clock = useBranchClock(tz);
  const name = state.roster[providerHandle]?.displayName ?? providerHandle;
  const eligibleWorking = ranking.working.filter((c) => c.exclusions.length === 0);
  const eligibleNotWorking = ranking.notWorking.filter((c) => c.exclusions.length === 0);
  const toggleTrait = (t: string) => setRequiredTraits((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t]));

  return (
    <Box sx={{ position: "fixed", right: 0, top: 72, bottom: 16, zIndex: (t) => t.zIndex.drawer - 1, display: "flex", alignItems: "stretch", pointerEvents: "none" }} data-testid="coverage-drawer">
      <Box sx={{ pointerEvents: "auto", display: "flex", alignItems: "flex-start", pt: 1 }}>
        <Tooltip title={open ? "Hide coverage" : "Show coverage"}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)} sx={{ bgcolor: "#fff", border: "1px solid #ddd", borderRight: 0, borderRadius: "8px 0 0 8px", boxShadow: 1 }} aria-label={open ? "hide coverage" : "show coverage"}>
            {open ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open} orientation="horizontal" sx={{ pointerEvents: "auto", height: "100%" }}>
        <Paper elevation={6} sx={{ width: 440, height: "100%", overflowY: "auto", p: 2, borderRadius: "8px 0 0 8px", display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={600}>
                Coverage · {name} called out
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              at {clock.time(startedAtIso)} · shift canceled · {ranking.visits.length} visit{ranking.visits.length === 1 ? "" : "s"} from {ranking.visits[0] ? clock.time(ranking.visits[0].startIso) : "—"} need{ranking.visits.length === 1 ? "s" : ""} cover
            </Typography>
          </Box>

          <WorklistRows rows={unfilled} compact selectedId={selectedRowId} onSelect={selectRow} />

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              Requires:
            </Typography>
            {state.config.traitVocabulary.map((t) => (
              <Chip key={t} size="small" label={t} color={requiredTraits.includes(t) ? "primary" : "default"} variant={requiredTraits.includes(t) ? "filled" : "outlined"} onClick={() => toggleTrait(t)} />
            ))}
          </Stack>

          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Working today · {eligibleWorking.length} eligible
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }} data-pool="working">
              {ranking.working.map((c) => (
                <CandidateCard key={c.handle} c={c} />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Not working · {eligibleNotWorking.length} eligible
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }} data-pool="notWorking">
              {ranking.poolOrder.map((pool) => {
                const inPool = ranking.notWorking.filter((c) => c.pool === pool);
                if (!inPool.length) return null;
                return (
                  <Box key={pool}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {POOL_LABELS[pool]}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                      {inPool.map((c) => (
                        <CandidateCard key={c.handle} c={c} />
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}


function CandidateCard({ c }: { c: Ranked }) {
  const eligible = c.exclusions.length === 0;
  return (
    <Paper variant="outlined" sx={{ p: 1, opacity: eligible ? 1 : 0.6 }} data-candidate={c.handle} data-eligible={eligible}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          {c.displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {c.discipline} · {c.employment === "perDiem" ? "external" : c.employment}
        </Typography>
        {c.ceilingImpact && <Chip size="small" color={c.ceilingImpact.overCeiling ? "error" : "default"} variant="outlined" label={`${c.ceilingImpact.pointsAfter} of ${c.ceilingImpact.ceilingPoints} points after`} sx={{ height: 18, fontSize: 10.5 }} />}
      </Stack>
      <Typography variant="caption" color={eligible ? "text.secondary" : "error"} sx={{ display: "block" }}>
        {eligible ? c.reasons.join(" · ") : c.exclusions.join(" · ")}
      </Typography>
    </Paper>
  );
}
